import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronDown,
  Phone,
  Stethoscope,
  User,
} from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { API } from "@/lib/booking";

const AVAILABILITY_DAYS = 60;

// Step order follows the API: the booking hold (and therefore the OTP) is only
// created once the patient's details have been submitted, so Details now
// precedes Verification and Payment is gone entirely.
const STEPS = ["Path", "Service & Time", "Your Details", "Verify Phone", "Confirmation"];

// Convert a local JS Date to a YYYY-MM-DD string (avoids UTC-shift issues)
function toLocalISO(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Slots carry ISO-8601 instants (startsAt / endsAt) only. Display them in the
// visitor's local time — the raw startsAt value is what gets submitted later.
function formatSlotTime(startsAt) {
  if (!startsAt) return "";
  const parsed = new Date(startsAt);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function BookingModal({ open, onClose, initialAudience }) {
  const [step, setStep] = useState(0);
  const [audience, setAudience] = useState(null);

  // ── Services + provider from the API ─────────────────────────────────────
  // Tenancy comes from the Host header, so no siteKey is ever sent. Each brand
  // has exactly one active provider, which is the one used for the booking.
  const [services, setServices] = useState([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [servicesError, setServicesError] = useState("");
  const [providerId, setProviderId] = useState("");

  // ── Selected service (by API id) + availability ───────────────────────────
  const [serviceId, setServiceId] = useState("");
  const [availability, setAvailability] = useState(null);
  const [loadingAvailability, setLoadingAvailability] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");

  // ── Selected date + slot (a slot is { startsAt, endsAt }) ────────────────
  const [date, setDate] = useState(undefined);
  const [slot, setSlot] = useState(null);

  // ── Patient details ───────────────────────────────────────────────────────
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  // ── Booking flow ──────────────────────────────────────────────────────────
  // 1 Service & Time -> 2 Your Details -> 3 Verify Phone (OTP) -> confirmed.
  // POST /public/bookings creates the hold *and* issues the OTP in one call.
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [otpInput, setOtpInput] = useState("");
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState("");
  // Live hold returned by POST /public/bookings — its OTP confirms it.
  const [hold, setHold] = useState(null); // { bookingId, reference, holdExpiresAt, phone }
  const [confirmation, setConfirmation] = useState(null); // { reference }
  const otpRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (!open) {
      return;
    }

    setStep(initialAudience ? 1 : 0);
    setAudience(initialAudience || null);
    setServiceId("");
    setAvailability(null);
    setAvailabilityError("");
    setDate(undefined);
    setSlot(null);
    setName("");
    setEmail("");
    setPhone("");
    setOtpInput("");
    setOtpError("");
    setHold(null);
    setConfirmation(null);
    setErrors({});
    setLoading(false);
    setOtpVerifying(false);
  }, [open, initialAudience]);

  const isPatient = audience === "patient";
  const accent = isPatient ? "#D4AF37" : "#1CB0C6";
  const accentBg = isPatient ? "#16110F" : "#082025";

  const selectedService = useMemo(
    () => services.find((entry) => entry.id === serviceId) || null,
    [services, serviceId]
  );

  // Slots for the currently selected date, keyed by the day's local date
  const todaySlots = useMemo(() => {
    if (!availability || !date) return [];
    const key = toLocalISO(date);
    return availability.days?.find((d) => d.date === key)?.slots || [];
  }, [availability, date]);

  // Day -> slot count, used to disable days with nothing free
  const availabilityMap = useMemo(() => {
    const map = {};
    for (const day of availability?.days ?? []) {
      map[day.date] = day.slots?.length ?? 0;
    }
    return map;
  }, [availability]);

  // ── Fetch services + providers on mount ───────────────────────────────────
  useEffect(() => {
    let active = true;
    setLoadingServices(true);
    setServicesError("");

    const getJson = (url) =>
      fetch(url).then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      });

    Promise.all([
      getJson(`${API}/public/services`),
      getJson(`${API}/public/providers`),
    ])
      .then(([servicesData, providersData]) => {
        if (!active) return;
        const serviceList = Array.isArray(servicesData)
          ? servicesData
          : servicesData.services ?? [];
        const providerList = providersData?.providers ?? [];
        setServices(serviceList);
        setProviderId(providerList[0]?.id ?? "");
      })
      .catch(() => {
        if (active) {
          setServicesError("Unable to load appointment options. Please try again.");
        }
      })
      .finally(() => {
        if (active) setLoadingServices(false);
      });

    return () => {
      active = false;
    };
  }, []);

  // ── Fetch availability whenever service or provider is known ──────────────
  useEffect(() => {
    if (!serviceId || !providerId) return;

    let active = true;
    setLoadingAvailability(true);
    setAvailabilityError("");
    setAvailability(null);
    setDate(undefined);
    setSlot(null);

    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + AVAILABILITY_DAYS);

    const params = new URLSearchParams({
      providerId,
      serviceId,
      from: toLocalISO(today),
      to: toLocalISO(endDate),
    });

    fetch(`${API}/public/availability?${params}`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        if (active) setAvailability(data);
      })
      .catch(() => {
        if (active) {
          setAvailabilityError("Unable to load availability. Please try again.");
        }
      })
      .finally(() => {
        if (active) setLoadingAvailability(false);
      });

    return () => {
      active = false;
    };
  }, [serviceId, providerId]);

  // ── Refetch availability (used after a 409 conflict) ──────────────────────
  const refetchAvailability = useCallback(() => {
    if (!serviceId || !providerId) return;
    setLoadingAvailability(true);
    setAvailabilityError("");

    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + AVAILABILITY_DAYS);

    const params = new URLSearchParams({
      providerId,
      serviceId,
      from: toLocalISO(today),
      to: toLocalISO(endDate),
    });

    fetch(`${API}/public/availability?${params}`)
      .then((r) => r.json())
      .then((data) => setAvailability(data))
      .catch(() => setAvailabilityError("Unable to refresh availability."))
      .finally(() => setLoadingAvailability(false));
  }, [serviceId, providerId]);

  // ── Validation ────────────────────────────────────────────────────────────
  const validateStep = (currentStep) => {
    const nextErrors = {};

    if (currentStep === 1) {
      if (!serviceId) {
        nextErrors.service = "Please select a service";
      } else if (!providerId) {
        nextErrors.service = "Booking is temporarily unavailable. Please try again.";
      } else if (!date) {
        nextErrors.date = "Please select an available date.";
      } else if (!slot) {
        nextErrors.slot = "Please select a time slot.";
      }
    }

    if (currentStep === 2) {
      // The API needs both a first and a last name; the single Full Name field
      // is split on whitespace.
      const parts = name.trim().split(/\s+/).filter(Boolean);
      if (!name.trim()) {
        nextErrors.name = "Full name is required";
      } else if (parts.length < 2) {
        nextErrors.name = "Please enter your first and last name";
      }
      if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
        nextErrors.email = "Valid email required";
      }
      if (!phone.trim() || phone.trim().length < 7) {
        nextErrors.phone = "Enter a valid phone number";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // ── Create the hold (this call is also what sends the OTP) ────────────────
  const createHold = async () => {
    if (!validateStep(2)) {
      return;
    }

    // A hold is already live for this slot. Posting again would collide with
    // our own reservation (the API refuses overlapping holds with a 409), so
    // go straight back to the code step with the hold we already have.
    if (hold?.bookingId) {
      setOtpInput("");
      setOtpError("");
      setStep(3);
      return;
    }

    setLoading(true);
    setErrors({});
    try {
      const parts = name.trim().split(/\s+/).filter(Boolean);
      const firstName = parts[0];
      const lastName = parts.slice(1).join(" ");

      const response = await fetch(`${API}/public/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          providerId,
          serviceId,
          // The slot's startsAt goes to the API verbatim: the server owns the
          // interpretation of the instant, so there is no timezone maths here.
          startsAt: slot?.startsAt,
          patient: {
            firstName,
            lastName,
            email: email.trim(),
            phone: phone.trim(),
          },
        }),
      });

      // 409 — the slot was taken between picking it and submitting.
      if (response.status === 409) {
        setSlot(null);
        setDate(undefined);
        refetchAvailability();
        setStep(1);
        setErrors({
          conflict: "That slot was just taken — please pick another time.",
        });
        return;
      }

      // 400 — validation errors from the backend.
      if (response.status === 400) {
        const errorBody = await response.json().catch(() => ({}));
        const fieldErrors = errorBody.errors || errorBody.fieldErrors || {};
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
        } else {
          setErrors({
            submit:
              errorBody.error ||
              errorBody.detail ||
              errorBody.message ||
              "Invalid booking details. Please check and try again.",
          });
        }
        return;
      }

      // 403 — the bot check refused the request.
      if (response.status === 403) {
        setErrors({
          submit:
            "We could not verify this request. Please refresh the page and try again.",
        });
        return;
      }

      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(
          errorBody.error || errorBody.detail || "Unable to create booking"
        );
      }

      const data = await response.json();
      setHold({
        bookingId: data.bookingId,
        reference: data.reference,
        holdExpiresAt: data.holdExpiresAt,
        // The code goes to the number on the booking, so show that number.
        phone: phone.trim(),
      });
      setOtpInput("");
      setOtpError("");
      setStep(3);
    } catch (err) {
      setErrors({
        submit:
          err?.message ||
          "We could not reach the booking service. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Confirm the hold with the 6-digit code ────────────────────────────────
  const confirmOtp = async () => {
    if (!hold?.bookingId) return;
    if (!/^\d{6}$/.test(otpInput)) {
      setOtpError("Enter the 6-digit code");
      return;
    }

    setOtpVerifying(true);
    setOtpError("");
    try {
      const response = await fetch(
        `${API}/public/bookings/${hold.bookingId}/confirm`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ code: otpInput }),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success !== false) {
        setConfirmation({ reference: data.reference || hold.reference });
        setStep(4);
        return;
      }

      // 429 — too many wrong codes; the API has released the hold.
      if (response.status === 429) {
        setOtpError(
          data.error ||
            "Too many incorrect codes, so this reservation has been released. Please start a new booking."
        );
        return;
      }

      // 409/410 — the hold is no longer awaiting confirmation, or it expired.
      if (response.status === 409 || response.status === 410) {
        setHold(null);
        setOtpInput("");
        setOtpError("");
        setSlot(null);
        setDate(undefined);
        refetchAvailability();
        setStep(1);
        setErrors({
          conflict:
            "Your reservation expired before the code was confirmed, so that time is no longer held. Please pick another time.",
        });
        return;
      }

      // 400 — the code was wrong or malformed.
      if (response.status === 400) {
        setOtpError(
          data.error || "That code is not correct. Please check it and try again."
        );
        return;
      }

      // 404 — the hold is gone entirely; start again.
      if (response.status === 404) {
        setHold(null);
        setOtpInput("");
        setOtpError("");
        setStep(1);
        setErrors({
          conflict:
            "We could not find this booking any more. Please choose a time again.",
        });
        return;
      }

      setOtpError(data.error || "Unable to verify the code. Please try again.");
    } catch (err) {
      setOtpError(
        "We could not reach the booking service. Please check your connection and try again."
      );
    } finally {
      setOtpVerifying(false);
    }
  };

  const handleOtpKey = (event, index) => {
    const value = event.target.value.replace(/\D/g, "");
    const nextInput = otpInput.split("");
    nextInput[index] = value;
    const joined = nextInput.join("").slice(0, 6);
    setOtpInput(joined);

    if (value && index < 5) {
      otpRefs[index + 1].current?.focus();
    }
    if (!value && index > 0) {
      otpRefs[index - 1].current?.focus();
    }

    setOtpError("");
  };

  const handleNext = () => {
    // Step 2 is the last one before the hold is created: submitting it POSTs
    // the booking (which is also what sends the code) rather than just moving on.
    if (step === 2) {
      createHold();
      return;
    }
    if (!validateStep(step)) {
      return;
    }
    setStep((currentStep) => currentStep + 1);
  };

  const inputBase = {
    width: "100%",
    padding: "10px 14px",
    fontFamily: "Outfit, sans-serif",
    fontSize: "14px",
    background: "#FAFAF9",
    color: "#1C1917",
    outline: "none",
    borderRadius: 0,
    transition: "border-color 0.2s",
  };

  const inputStyle = (field) => ({
    ...inputBase,
    border: `1px solid ${errors[field] ? "#ef4444" : "#E7E5E4"}`,
  });

  const currentStepLabel = step < 5 ? STEPS[Math.min(step, 4)] : "Complete";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent
        data-testid="booking-modal"
        className="max-w-xl gap-0 overflow-hidden p-0"
        style={{ borderRadius: 0, border: "none", fontFamily: "Outfit, sans-serif", maxHeight: "95vh" }}
      >
        <div
          className="flex items-center justify-between px-7 py-5"
          style={{ background: step === 0 ? "#1C1917" : accentBg }}
        >
          <div>
            <p
              className="mb-0.5 font-outfit text-xs uppercase tracking-[0.2em]"
              style={{ color: accent || "#D4AF37", opacity: 0.8 }}
            >
              {currentStepLabel}
            </p>
            <h2 className="font-cormorant text-2xl font-light" style={{ color: "#FDF8ED" }}>
              {step === 0 && "Who's booking?"}
              {step === 1 && (isPatient ? "Select Treatment" : "Select Course")}
              {step === 2 && "Your Details"}
              {step === 3 && "Verify Your Number"}
              {step === 4 && "Booking Confirmed"}
            </h2>
          </div>

          {step > 0 && step < 5 && (
            <div className="ml-4 flex items-center gap-1.5">
              {[1, 2, 3, 4].map((indicator) => (
                <div
                  key={indicator}
                  style={{
                    height: "8px",
                    background:
                      indicator < step
                        ? "#78716C"
                        : indicator === step
                          ? accent
                          : "rgba(255,255,255,0.15)",
                    width: indicator === step ? "20px" : "8px",
                    borderRadius: indicator === step ? "4px" : "50%",
                    transition: "all 0.3s ease",
                  }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="overflow-y-auto px-7 py-7" style={{ maxHeight: "60vh", background: "#FFFFFF" }}>
          {step === 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {[
                {
                  id: "patient",
                  icon: <User size={20} />,
                  bg: "#16110F",
                  accent: "#D4AF37",
                  title: "I'm a Patient",
                  sub: "Book a specialist dental consultation at Specialist Dental London",
                  cta: "Book Consultation",
                },
                {
                  id: "professional",
                  icon: <Stethoscope size={20} />,
                  bg: "#082025",
                  accent: "#1CB0C6",
                  title: "I'm a Clinician",
                  sub: "Reserve a place on an advanced clinical training course",
                  cta: "Reserve a Place",
                },
              ].map((option) => (
                <button
                  key={option.id}
                  data-testid={`booking-choose-${option.id}`}
                  onClick={() => {
                    setAudience(option.id);
                    setStep(1);
                  }}
                  className="group flex flex-col items-start gap-4 border-2 p-6 text-left transition-all duration-200"
                  style={{ border: "2px solid #E7E5E4" }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.borderColor = option.accent;
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.borderColor = "#E7E5E4";
                  }}
                >
                  <div className="flex h-10 w-10 items-center justify-center" style={{ background: option.bg, color: option.accent }}>
                    {option.icon}
                  </div>
                  <div>
                    <h3 className="mb-1 font-cormorant text-xl font-medium" style={{ color: "#1C1917" }}>
                      {option.title}
                    </h3>
                    <p className="font-outfit text-sm leading-relaxed" style={{ color: "#78716C" }}>
                      {option.sub}
                    </p>
                  </div>
                  <span className="flex items-center gap-1.5 font-outfit text-xs font-medium" style={{ color: option.accent }}>
                    {option.cta} <ArrowRight size={12} />
                  </span>
                </button>
              ))}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="mb-2 block font-outfit text-xs uppercase tracking-wider" style={{ color: "#78716C" }}>
                  {isPatient ? "Treatment" : "Course"} *
                </label>
                <div className="relative">
                  <select
                    data-testid="booking-service-select"
                    value={serviceId}
                    onChange={(event) => {
                      setServiceId(event.target.value);
                      setErrors({});
                    }}
                    disabled={loadingServices || !!servicesError}
                    style={{ ...inputStyle("service"), appearance: "none", paddingRight: "36px", cursor: "pointer" }}
                  >
                    <option value="">
                      {loadingServices ? "Loading…" : "- Select -"}
                    </option>
                    {services.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.name}
                        {entry.depositPence > 0 ? ` - GBP ${(entry.depositPence / 100).toFixed(2)} deposit` : ""}
                      </option>
                    ))}
                  </select>
                  <ChevronDown
                    size={14}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                    style={{ color: "#78716C" }}
                  />
                </div>
                {errors.service && <p className="mt-1 font-outfit text-xs text-red-500">{errors.service}</p>}
                {servicesError && <p className="mt-1 font-outfit text-xs text-red-500">{servicesError}</p>}
              </div>

              <div>
                <label className="mb-2 block font-outfit text-xs uppercase tracking-wider" style={{ color: "#78716C" }}>
                  Preferred Date
                </label>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={(value) =>
                    value < new Date() ||
                    value.getDay() === 0 ||
                    (availabilityMap[toLocalISO(value)] ?? 0) === 0
                  }
                  className="border"
                  style={{ borderColor: "#E7E5E4", fontFamily: "Outfit, sans-serif", fontSize: "13px" }}
                />
                {loadingAvailability && (
                  <p className="mt-1 font-outfit text-xs text-stone-500">Loading availability…</p>
                )}
                {availabilityError && (
                  <p className="mt-1 font-outfit text-xs text-red-500">{availabilityError}</p>
                )}
                {errors.date && <p className="mt-1 font-outfit text-xs text-red-500">{errors.date}</p>}
              </div>

              {date && (
                <div>
                  <label className="mb-2 block font-outfit text-xs uppercase tracking-wider" style={{ color: "#78716C" }}>
                    Preferred Time
                  </label>
                  {todaySlots.length === 0 ? (
                    <p className="font-outfit text-xs text-stone-500">
                      No appointment slots are available for this date. Please choose another day.
                    </p>
                  ) : (
                    <div className="grid grid-cols-5 gap-2">
                      {todaySlots.map((entry) => (
                        <button
                          key={entry.startsAt}
                          data-testid={`time-slot-${formatSlotTime(entry.startsAt)}`}
                          onClick={() => {
                            setSlot(entry);
                            setErrors({});
                          }}
                          className="border py-2 text-center font-outfit text-xs transition-all duration-150"
                          style={{
                            border: `1px solid ${slot?.startsAt === entry.startsAt ? accent : "#E7E5E4"}`,
                            background: slot?.startsAt === entry.startsAt ? (isPatient ? "#16110F" : "#082025") : "#FAFAF9",
                            color: slot?.startsAt === entry.startsAt ? accent : "#78716C",
                          }}
                        >
                          {formatSlotTime(entry.startsAt)}
                        </button>
                      ))}
                    </div>
                  )}
                  {errors.slot && <p className="mt-1 font-outfit text-xs text-red-500">{errors.slot}</p>}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <label className="mb-1.5 block font-outfit text-xs uppercase tracking-wider" style={{ color: "#78716C" }}>
                  Full Name *
                </label>
                <input
                  data-testid="booking-name-input"
                  type="text"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setErrors((current) => ({ ...current, name: "" }));
                  }}
                  placeholder="Dr. Jane Smith"
                  style={inputStyle("name")}
                  onFocus={(event) => {
                    event.target.style.borderColor = accent;
                  }}
                  onBlur={(event) => {
                    event.target.style.borderColor = errors.name ? "#ef4444" : "#E7E5E4";
                  }}
                />
                {errors.name && <p className="mt-1 font-outfit text-xs text-red-500">{errors.name}</p>}
              </div>

              <div>
                <label className="mb-1.5 block font-outfit text-xs uppercase tracking-wider" style={{ color: "#78716C" }}>
                  Email Address *
                </label>
                <input
                  data-testid="booking-email-input"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setErrors((current) => ({ ...current, email: "" }));
                  }}
                  placeholder="jane@example.com"
                  style={inputStyle("email")}
                  onFocus={(event) => {
                    event.target.style.borderColor = accent;
                  }}
                  onBlur={(event) => {
                    event.target.style.borderColor = errors.email ? "#ef4444" : "#E7E5E4";
                  }}
                />
                {errors.email && <p className="mt-1 font-outfit text-xs text-red-500">{errors.email}</p>}
              </div>

              <div>
                <label className="mb-1.5 block font-outfit text-xs uppercase tracking-wider" style={{ color: "#78716C" }}>
                  Mobile Number *
                </label>
                <input
                  data-testid="booking-phone-input"
                  type="tel"
                  value={phone}
                  onChange={(event) => {
                    setPhone(event.target.value);
                    setErrors((current) => ({ ...current, phone: "" }));
                  }}
                  placeholder="+44 7700 000000"
                  style={inputStyle("phone")}
                  onFocus={(event) => {
                    event.target.style.borderColor = accent;
                  }}
                  onBlur={(event) => {
                    event.target.style.borderColor = errors.phone ? "#ef4444" : "#E7E5E4";
                  }}
                />
                {errors.phone && <p className="mt-1 font-outfit text-xs text-red-500">{errors.phone}</p>}
              </div>

              <div className="p-4" style={{ background: "#FAFAF9", border: "1px solid #E7E5E4" }}>
                <p className="font-outfit text-xs" style={{ color: "#78716C" }}>
                  Your details are used solely to confirm your booking. We will never share your information with third parties.
                </p>
              </div>

              {errors.submit && <p className="text-center font-outfit text-sm text-red-500">{errors.submit}</p>}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="py-4 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center" style={{ background: accentBg }}>
                  <Phone size={22} style={{ color: accent }} />
                </div>
                <p className="font-outfit text-sm leading-relaxed" style={{ color: "#78716C" }}>
                  We've sent a 6-digit code to <strong>{hold?.phone || phone}</strong> to confirm your booking.
                  <button
                    onClick={() => {
                      setOtpInput("");
                      setOtpError("");
                      setStep(2);
                    }}
                    className="ml-2 font-outfit text-xs underline"
                    style={{ color: accent }}
                  >
                    Change
                  </button>
                </p>
                {hold?.holdExpiresAt && (
                  <p className="mt-1 font-outfit text-xs" style={{ color: "#78716C" }}>
                    Enter it by {formatSlotTime(hold.holdExpiresAt)} to keep this time.
                  </p>
                )}
              </div>

              <div>
                <label className="mb-3 block font-outfit text-xs uppercase tracking-wider" style={{ color: "#78716C" }}>
                  Enter Code
                </label>
                <div className="mb-4 flex justify-center gap-3">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                      key={index}
                      ref={otpRefs[index]}
                      data-testid={`otp-input-${index}`}
                      type="text"
                      maxLength={1}
                      value={otpInput[index] || ""}
                      onChange={(event) => handleOtpKey(event, index)}
                      className="text-center font-outfit text-xl font-medium transition-all duration-200"
                      style={{
                        width: "52px",
                        height: "56px",
                        border: `2px solid ${otpError ? "#ef4444" : otpInput[index] ? accent : "#E7E5E4"}`,
                        background: "#FAFAF9",
                        color: "#1C1917",
                        outline: "none",
                        borderRadius: 0,
                      }}
                    />
                  ))}
                </div>

                {otpError && <p className="mb-3 text-center font-outfit text-xs text-red-500">{otpError}</p>}

                <button
                  data-testid="verify-otp-btn"
                  onClick={confirmOtp}
                  disabled={otpVerifying}
                  className="w-full py-3 font-outfit text-sm font-medium transition-all duration-200 hover:opacity-90 disabled:opacity-50"
                  style={{ background: accent, color: accentBg }}
                >
                  {otpVerifying ? "Verifying..." : "Verify & Confirm"}
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5">
              <div className="flex flex-col items-center gap-3 py-2">
                <div className="flex h-14 w-14 items-center justify-center" style={{ background: accentBg }}>
                  <Check size={24} style={{ color: accent }} />
                </div>
                <p className="font-cormorant text-2xl font-light" style={{ color: "#1C1917" }}>
                  Booking Confirmed
                </p>
                <p className="max-w-xs text-center font-outfit text-sm leading-relaxed" style={{ color: "#78716C" }}>
                  Your appointment is booked. Keep the reference below — the clinic will ask for it if you need to change anything.
                </p>
              </div>

              {confirmation?.reference && (
                <div className="px-6 py-4 text-center" style={{ border: `1px solid ${accent}`, background: "#FAFAF9" }}>
                  <span className="mb-1 block font-outfit text-xs uppercase tracking-widest" style={{ color: "#78716C" }}>
                    Booking Reference
                  </span>
                  <span
                    data-testid="booking-reference"
                    className="font-outfit text-xl font-medium tracking-wider"
                    style={{ color: accent }}
                  >
                    {confirmation.reference}
                  </span>
                </div>
              )}

              <div className="p-5" style={{ background: "#FAFAF9", border: "1px solid #E7E5E4" }}>
                <h4 className="mb-4 font-cormorant text-lg font-medium" style={{ color: "#1C1917" }}>
                  Booking Summary
                </h4>
                <dl className="space-y-2.5">
                  {[
                    { label: "Type", value: isPatient ? "Patient Consultation" : "Academy Course" },
                    { label: "Service", value: selectedService?.name },
                    {
                      label: "Date",
                      value: date ? date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long" }) : "Flexible",
                    },
                    { label: "Time", value: slot ? formatSlotTime(slot.startsAt) : "Flexible" },
                    { label: "Patient", value: name },
                    { label: "Phone", value: phone },
                  ].map((item) => (
                    <div key={item.label} className="flex gap-4">
                      <dt className="w-20 shrink-0 pt-0.5 font-outfit text-xs uppercase tracking-wider" style={{ color: "#78716C" }}>
                        {item.label}
                      </dt>
                      <dd className="font-outfit text-sm" style={{ color: "#1C1917" }}>
                        {item.value}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between px-7 py-5" style={{ background: "#FAFAF9", borderTop: "1px solid #E7E5E4" }}>
          <button
            data-testid="booking-back-btn"
            onClick={() => setStep((currentStep) => Math.max(0, currentStep - 1))}
            disabled={step === 0}
            className="flex items-center gap-2 font-outfit text-sm transition-all duration-200 hover:opacity-60 disabled:opacity-20"
            style={{ color: "#78716C" }}
          >
            <ArrowLeft size={15} /> Back
          </button>

          {step < 4 ? (
            <button
              data-testid="booking-next-btn"
              onClick={step === 0 ? undefined : step === 3 ? confirmOtp : handleNext}
              disabled={(step === 0 && !audience) || (step === 2 && loading) || (step === 3 && otpVerifying)}
              className="flex items-center gap-2 px-6 py-3 font-outfit text-sm font-medium transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-30"
              style={{ background: step === 0 ? "#1C1917" : accentBg, color: step === 0 ? "#FAFAF9" : accent }}
            >
              {step === 2 && loading
                ? "Reserving..."
                : step === 3
                  ? otpVerifying
                    ? "Confirming..."
                    : "Confirm Booking"
                  : "Continue"}{" "}
              <ArrowRight size={14} />
            </button>
          ) : (
            <button
              data-testid="booking-done-btn"
              onClick={onClose}
              className="flex items-center gap-3 px-7 py-3 font-outfit text-sm font-medium transition-all duration-200 hover:opacity-90 active:scale-95 disabled:opacity-50"
              style={{ background: accent, color: accentBg }}
            >
              Done
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
